import { useState } from 'react'
import { FileDown, FileSpreadsheet, Table } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { exportToCsv, ColumnDefinition, ExportMetadata } from '../utils/exportToCsv'
import { exportToPdf } from '../utils/exportToPdf'
import { exportToExcel } from '../utils/exportToExcel'

interface ExportButtonsProps {
  data: any[]
  columns: ColumnDefinition[]
  metadata: ExportMetadata
  disabled?: boolean
  /** Optional callback to capture a chart image for embedding in the PDF */
  onGetChartImage?: () => string | undefined
  /** Fetch the rows on click instead of exporting `data` — for a paged table
   *  whose export holds more than the page on screen. `data` still decides
   *  whether the buttons are enabled. A rejection exports nothing; the
   *  caller reports its own failure. */
  loadData?: () => Promise<unknown[]>
}

export default function ExportButtons({ data, columns, metadata, disabled = false, onGetChartImage, loadData }: ExportButtonsProps) {
  const { t } = useTranslation('components')
  const [loading, setLoading] = useState(false)
  const isDisabled = disabled || loading || !data || data.length === 0

  const run = async (exportRows: (rows: unknown[]) => void) => {
    if (isDisabled) return
    if (!loadData) { exportRows(data); return }
    setLoading(true)
    try {
      exportRows(await loadData())
    } catch {
      // reported by the caller's loadData
    } finally {
      setLoading(false)
    }
  }

  const handleCsvExport = () => run(rows => exportToCsv(rows, columns, metadata))

  const handleExcelExport = () => run(rows => exportToExcel(rows, columns, metadata))

  const handlePdfExport = () => run(rows => {
    const chartImage = onGetChartImage?.() ?? undefined
    exportToPdf(rows, columns, { ...metadata, chartImage })
  })

  return (
    <div className="flex gap-2">
      <button
        onClick={handleCsvExport}
        disabled={isDisabled}
        className="btn-secondary flex items-center gap-2 text-sm"
        title={t('exportButtons.exportTo', { format: 'CSV' })}
      >
        <FileSpreadsheet className="w-4 h-4" />
        CSV
      </button>
      <button
        onClick={handleExcelExport}
        disabled={isDisabled}
        className="btn-secondary flex items-center gap-2 text-sm"
        title={t('exportButtons.exportTo', { format: 'Excel' })}
      >
        <Table className="w-4 h-4" />
        Excel
      </button>
      <button
        onClick={handlePdfExport}
        disabled={isDisabled}
        className="btn-secondary flex items-center gap-2 text-sm"
        title={t('exportButtons.exportTo', { format: 'PDF' })}
      >
        <FileDown className="w-4 h-4" />
        PDF
      </button>
    </div>
  )
}
